var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Component, Vue } from 'vue-property-decorator';
import { getRebornUrl } from '@/shared/utils/helpers';
let InfosetReborn = class InfosetReborn extends Vue {
    constructor() {
        super(...arguments);
        this.retryTimers = [];
        // Polling de token — ativo apenas durante recuperação de sessão
        this.tokenPollTimer = null;
        // Último token enviado — detecta quando o login atualiza o token
        this.lastSentToken = '';
    }
    /**
     * URL do iframe — sem token na URL; ele vai via postMessage COMPANY_INIT.
     *
     * Mesma convenção de `ShopReborn.vue`: a rota desta página no V3
     * (`/infosets/:infosetId`, e `/infosets/:infosetId/companies/:companyId`
     * no link direto para uma ficha) tem exatamente a mesma forma no reborn,
     * então basta repassar o `fullPath` inteiro — ele já leva a query (hoje,
     * `view_as`/`view_as_name`) sem precisar traduzir nada.
     */
    get iframeSrc() {
        return `${getRebornUrl()}${this.$route.fullPath}`;
    }
    /**
     * Envia o token atual para o iframe React via postMessage.
     *
     * Mesmos campos de `InfosetsReborn.vue` (`accountId`, `role`, `email`): o
     * reborn não tem `/user/me` e esta é a única fonte para quem pode
     * compartilhar, ver "Visualizar como" etc.
     *
     * `features` (as flags da conta, como no `SearchReborn.vue`) decide quais
     * botões a campanha desenha — hoje, o do WhatsApp (`enrichment_whatsapp`), que
     * na V3 também só existe para quem tem a flag. Também só chega por aqui: sem
     * o campo, o reborn não liga o botão.
     */
    sendInit() {
        const iframe = this.$refs.iframe;
        if (!iframe || !iframe.contentWindow)
            return;
        const token = localStorage.getItem('auth_token_default') || '';
        this.lastSentToken = token;
        // @ts-ignore TS2349: $auth injetado pelo vue-auth
        const user = this.$auth.user() || {};
        iframe.contentWindow.postMessage({
            type: 'COMPANY_INIT',
            token,
            accountId: user.account_id || '',
            role: user.role || '',
            email: user.email || '',
            features: Array.isArray(user.features) ? user.features : [],
        }, '*');
    }
    /** Chamado quando o iframe termina de carregar — inicia o handshake. */
    onIframeLoad() {
        this.stopSessionRecovery();
        this.sendInit();
        // Retentativas para cobrir race condition entre @load e o React estar pronto
        this.retryTimers.push(setTimeout(() => this.sendInit(), 500));
        this.retryTimers.push(setTimeout(() => this.sendInit(), 2000));
    }
    /**
     * Inicia a recuperação de sessão após receber SESSION_EXPIRED do iframe.
     * Envia o token atual imediatamente e começa a monitorar o localStorage para
     * detectar quando o usuário fizer login e o token for atualizado.
     */
    startSessionRecovery() {
        if (this.tokenPollTimer)
            return; // já em progresso
        // Envia o token atual (pode ainda estar expirado — o React tentará e
        // falhará, mas ficará aguardando um COMPANY_INIT com token válido).
        this.sendInit();
        // Monitora auth_token_default a cada 500 ms; quando mudar, envia novo token.
        this.tokenPollTimer = setInterval(() => {
            const current = localStorage.getItem('auth_token_default') || '';
            if (current && current !== this.lastSentToken) {
                this.sendInit();
                this.stopSessionRecovery();
            }
        }, 500);
        // Para o polling automaticamente após 10 minutos para não vazar memória.
        this.retryTimers.push(setTimeout(() => this.stopSessionRecovery(), 10 * 60 * 1000));
    }
    stopSessionRecovery() {
        if (this.tokenPollTimer) {
            clearInterval(this.tokenPollTimer);
            this.tokenPollTimer = null;
        }
    }
    /** Ouve postMessages do iframe React. */
    handleMessage(event) {
        if (!event.data)
            return;
        // React sinaliza que está pronto para receber o token
        if (event.data.type === 'IFRAME_READY') {
            this.sendInit();
            return;
        }
        // React detectou 401/403 — inicia recuperação aguardando novo login
        if (event.data.type === 'SESSION_EXPIRED') {
            this.startSessionRecovery();
            return;
        }
        // React pede uma tela que ainda vive aqui fora do iframe, ou uma troca
        // de página do reborn (outro iframe, outra página do V3) — ex.: "Minhas
        // Listas" de volta para a listagem.
        if (event.data.type === 'NAVIGATE_PARENT') {
            this.navigateFromIframe(event);
        }
    }
    /**
     * Navega para uma tela do V3 a pedido do iframe.
     *
     * O reborn não consegue chegar sozinho: mexer em `window.top.location` de
     * dentro de um iframe cross-origin é bloqueado pelo browser. Ele manda o
     * *nome* da rota, não uma URL — assim o React não precisa conhecer a forma
     * dos caminhos daqui — e o push passa pelos guards de sempre.
     *
     * Diferente das outras mensagens, esta valida a origem: qualquer iframe da
     * página pode postar para cá, e tirar o usuário da tela a pedido de um
     * terceiro não é algo para se aceitar às cegas.
     */
    navigateFromIframe(event) {
        if (event.origin !== getRebornUrl())
            return;
        const route = event.data.route;
        if (!route || !route.name)
            return;
        this.$router
            .push({
            name: route.name,
            params: route.params || {},
            query: route.query || {},
        })
            // Repetir a rota atual rejeita com NavigationDuplicated — não é erro.
            .catch(() => undefined);
    }
    /**
     * Some com o widget de chat (Jivo) enquanto a tela de campanha está
     * aberta — mesma regra da implementação antiga (`InfosetLegacy.vue`), que
     * continua valendo aqui porque é da PÁGINA, não da implementação.
     */
    handleJivoChatVisibility(visible) {
        const jivoDiv = document.getElementsByTagName('jdiv')[1];
        if (jivoDiv) {
            if (visible)
                jivoDiv.classList.add('hidden');
            else
                jivoDiv.classList.remove('hidden');
        }
    }
    created() {
        this.handleJivoChatVisibility(true);
    }
    mounted() {
        window.addEventListener('message', this.handleMessage);
    }
    beforeDestroy() {
        this.handleJivoChatVisibility(false);
        window.removeEventListener('message', this.handleMessage);
        this.stopSessionRecovery();
        this.retryTimers.forEach(clearTimeout);
        this.retryTimers = [];
    }
};
InfosetReborn = __decorate([
    Component({ name: 'InfosetReborn' })
], InfosetReborn);
export default InfosetReborn;
